import { useState } from 'react';
import {
	Minimize2,
	Maximize2,
	Mic,
	MicOff,
	Phone,
	PhoneCall,
	PhoneIncoming,
	PhoneMissed,
	PhoneOff,
	PhoneOutgoing,
	X,
	Delete
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePhone, type CallState } from './PhoneProvider';

function formatDuration(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${s.toString().padStart(2, '0')}`;
}

const dialpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

function DialStateIcon({ state, direction }: { state: CallState; direction?: string }) {
	switch (state) {
		case 'dialing':
			return <PhoneOutgoing className="h-5 w-5 animate-pulse text-accent" />;
		case 'ringing':
			return direction === 'inbound'
				? <PhoneIncoming className="h-5 w-5 animate-bounce text-success" />
				: <PhoneCall className="h-5 w-5 animate-pulse text-accent" />;
		case 'active':
			return <PhoneCall className="h-5 w-5 text-success" />;
		case 'ended':
			return <PhoneMissed className="h-5 w-5 text-danger" />;
		default:
			return <Phone className="h-5 w-5 text-muted" />;
	}
}

export function Softphone() {
	const {
		pbxEnabled,
		pbxHealthy,
		activeCall,
		dialpadOpen,
		minimized,
		dial,
		hangup,
		toggleDialpad,
		setMinimized
	} = usePhone();

	const [dialInput, setDialInput] = useState('');
	const [muted, setMuted] = useState(false);

	if (!pbxEnabled) return null;

	// Minimized floating button
	if (minimized && !activeCall) {
		return (
			<button
				onClick={() => setMinimized(false)}
				className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-primary shadow-lg transition hover:bg-accent hover:shadow-xl"
			>
				<Phone className="h-6 w-6" />
			</button>
		);
	}

	// Minimized with active call — small pill
	if (minimized && activeCall) {
		return (
			<button
				onClick={() => setMinimized(false)}
				className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-full bg-green-600 px-5 py-3 text-sm font-medium text-primary shadow-lg transition hover:bg-green-500"
			>
				<DialStateIcon state={activeCall.state} direction={activeCall.direction} />
				<span>{activeCall.contactName || activeCall.number}</span>
				<span className="tabular-nums">{formatDuration(activeCall.duration)}</span>
			</button>
		);
	}

	const handleDial = () => {
		if (dialInput.trim()) {
			void dial(dialInput.trim());
			setDialInput('');
		}
	};

	return (
		<div className="fixed bottom-6 right-6 z-50 w-80 overflow-hidden rounded-3xl border border-subtle bg-surface-2/95 shadow-2xl backdrop-blur-xl">
			{/* Header */}
			<div className="flex items-center justify-between border-b border-subtle px-4 py-3">
				<div className="flex items-center gap-2">
					<Phone className="h-4 w-4 text-accent" />
					<span className="text-sm font-semibold text-primary">Phone</span>
					<span
						className={cn(
							'h-2 w-2 rounded-full',
							pbxHealthy ? 'bg-green-400' : 'bg-red-400'
						)}
						title={pbxHealthy ? 'PBX connected' : 'PBX disconnected'}
					/>
				</div>
				<div className="flex items-center gap-1">
					<button
						onClick={() => setMinimized(true)}
						className="rounded-lg p-1.5 text-muted transition hover:bg-surface-hover hover:text-primary"
					>
						<Minimize2 className="h-3.5 w-3.5" />
					</button>
					<button
						onClick={() => setMinimized(true)}
						className="rounded-lg p-1.5 text-muted transition hover:bg-surface-hover hover:text-primary"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>

			{/* Active call */}
			{activeCall ? (
				<div className="px-4 py-5">
					<div className="text-center">
						<DialStateIcon state={activeCall.state} direction={activeCall.direction} />
						<p className="mt-2 text-lg font-semibold text-primary">
							{activeCall.contactName || activeCall.number}
						</p>
						{activeCall.contactName && (
							<p className="text-sm text-muted">{activeCall.number}</p>
						)}
						<p className="mt-1 text-xs uppercase tracking-wider text-muted">
							{activeCall.state === 'dialing' && 'Dialing...'}
							{activeCall.state === 'ringing' &&
								(activeCall.direction === 'inbound' ? 'Incoming call' : 'Ringing...')}
							{activeCall.state === 'active' && formatDuration(activeCall.duration)}
							{activeCall.state === 'held' && 'On hold'}
						</p>
					</div>

					<div className="mt-5 flex items-center justify-center gap-4">
						<button
							onClick={() => setMuted(!muted)}
							className={cn(
								'flex h-12 w-12 items-center justify-center rounded-full transition',
								muted
									? 'bg-danger/15 text-danger hover:bg-danger/20'
									: 'bg-surface-hover text-primary hover:bg-surface-hover'
							)}
						>
							{muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
						</button>

						<button
							onClick={hangup}
							className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-primary transition hover:bg-red-500"
						>
							<PhoneOff className="h-6 w-6" />
						</button>

						<button
							onClick={toggleDialpad}
							className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-hover text-primary transition hover:bg-surface-hover"
						>
							<span className="text-lg font-bold">#</span>
						</button>
					</div>
				</div>
			) : (
				/* Dialpad / idle */
				<div className="px-4 py-4">
					{/* Input */}
					<div className="flex items-center gap-2 rounded-2xl border border-subtle bg-canvas px-3 py-2">
						<input
							type="tel"
							value={dialInput}
							onChange={(e) => setDialInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleDial();
							}}
							placeholder="Enter number..."
							className="flex-1 bg-transparent text-base text-primary placeholder:text-muted focus:outline-none"
						/>
						{dialInput && (
							<button
								onClick={() => setDialInput((prev) => prev.slice(0, -1))}
								className="rounded-lg p-1 text-muted hover:text-primary"
							>
								<Delete className="h-4 w-4" />
							</button>
						)}
					</div>

					{/* Dialpad grid */}
					{(dialpadOpen || !activeCall) && (
						<div className="mt-3 grid grid-cols-3 gap-2">
							{dialpadKeys.map((key) => (
								<button
									key={key}
									onClick={() => setDialInput((prev) => prev + key)}
									className="flex h-12 items-center justify-center rounded-2xl border border-subtle bg-surface-2 text-lg font-medium text-primary transition hover:bg-surface-hover"
								>
									{key}
								</button>
							))}
						</div>
					)}

					{/* Call button */}
					<button
						onClick={handleDial}
						disabled={!dialInput.trim() || !pbxHealthy}
						className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 px-4 py-3 text-sm font-semibold text-primary transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-40"
					>
						<Phone className="h-4 w-4" />
						Call
					</button>
				</div>
			)}
		</div>
	);
}

// Click-to-call button for use in contact/company records
export function ClickToCallButton({
	number,
	contactId,
	companyId,
	contactName,
	size = 'sm'
}: {
	number: string;
	contactId?: number;
	companyId?: number;
	contactName?: string;
	size?: 'sm' | 'md';
}) {
	const { pbxEnabled, pbxHealthy, dial } = usePhone();

	if (!pbxEnabled) return null;

	return (
		<button
			onClick={() => void dial(number, contactId, companyId, contactName)}
			disabled={!pbxHealthy}
			title={pbxHealthy ? `Call ${number}` : 'PBX not connected'}
			className={cn(
				'inline-flex items-center gap-1.5 rounded-xl bg-success/15 text-success transition hover:bg-success/20 disabled:cursor-not-allowed disabled:opacity-40',
				size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
			)}
		>
			<Phone className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
			{size === 'md' && <span>Call</span>}
		</button>
	);
}
